import { AES, enc } from 'crypto-js';
import { AuthUserContext } from '../services/AuthUserContext';
import { UrlHelper } from '../utils/UrlHelper';

export class QueryParamManager {
  private static readonly KEY_ADDITIONAL_NUMBER = 1;

  /**
   * Parse JSON object from the query param.
   */
  extractQueryValue(key: string): unknown {
    try {
      const encryptedData = UrlHelper.getQueryParam(key, '');
      if (typeof encryptedData !== 'string' || encryptedData === '') {
        return null;
      }

      const rawData = AES.decrypt(encryptedData, this.getKey()).toString(enc.Utf8);
      if (rawData === '') {
        return null;
      }

      return JSON.parse(rawData);
    } catch {
      // if invalid filter map, return null
      return null;
    }
  }

  /**
   * Store value to the query param.
   */
  storeQueryValue(key: string, value: unknown, replaceState = false): void {
    const storingValue = value === '' ? '' : AES.encrypt(JSON.stringify(value), this.getKey()).toString();

    const currentUrl = window.location.pathname;
    const newUrlParams = new URLSearchParams();
    newUrlParams.set(key, storingValue);

    const newUrl = `${currentUrl}?${newUrlParams.toString()}`;
    if (replaceState) {
      window.history.replaceState(window.history.state, '', newUrl); // replace current history state
    } else {
      window.history.pushState({}, '', newUrl); // push into history state
    }
  }

  /**
   * Updates value of a query param. If blank value provided, we remove the param instead.
   */
  updateQueryValue(key: string, value: unknown, replaceState = false): void {
    // Extract all the existing params.
    const urlParams = new URLSearchParams(window.location.search);

    // Delete the current state in query params.
    urlParams.delete(key);

    if (value || value === false) {
      const storingValue = AES.encrypt(JSON.stringify(value), this.getKey()).toString();
      urlParams.set(key, storingValue);
    }

    const currentUrl = window.location.pathname;
    const newUrl = urlParams.size ? `${currentUrl}?${urlParams.toString()}` : currentUrl;

    if (replaceState) {
      window.history.replaceState(window.history.state, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }

  private getKey(): string {
    const baseId = AuthUserContext.getUser().getOrganizationUUID();

    /*
    Generate the key as a combination of hardcoded number and baseId.
    Combine them in a format of `{number}{id}{number}{id}` and slice that to the length od 32.

    AES-256 requires a key of length 32.

    If we need to invalidate all keys, change the KEY_ADDITIONAL_NUMBER to some other value.
     */
    return [QueryParamManager.KEY_ADDITIONAL_NUMBER, baseId, QueryParamManager.KEY_ADDITIONAL_NUMBER, baseId].join('').slice(0, 32);
  }
}
